/**
 * ScriptCache - prevents duplicate reports during the session
 *
 * - Reported / InFlight by (script_url, script_hash_or_empty). For third_party
 *   (no hash) the second arg is '' so the same external URL is only reported once.
 * - Processed (by element) to avoid loops with MutationObserver
 */
export class ScriptCache {
	constructor(config = null, logger = null) {
		this.config = config;
		this.logger = logger;

		this.reported = new Map();
		this.processedElements = new WeakSet();
		this.inFlight = new Set();

		this._storage = this._getStorage();
		this._storageKey = this._getStorageKey();
		this._ttlMs = this._getNumber('scriptIntegrityDedupeTtlMs', 0);
		this._maxEntries = Math.max(1, this._getNumber('scriptIntegrityDedupeMaxEntries', 500));

		this._loadReportedFromStorage();
	}

	makeKey(scriptUrl, scriptHash) {
		return `${scriptUrl || ''}::${scriptHash ?? ''}`;
	}

	isProcessed(scriptEl) {
		return !!scriptEl && this.processedElements.has(scriptEl);
	}

	markProcessed(scriptEl) {
		if (scriptEl) this.processedElements.add(scriptEl);
	}

	hasReported(scriptUrl, scriptHash) {
		const key = this.makeKey(scriptUrl, scriptHash);
		const ts = this.reported.get(key);
		if (!ts) return false;
		if (this._ttlMs > 0 && (Date.now() - ts) > this._ttlMs) {
			this.reported.delete(key);
			this._persistMaybe();
			return false;
		}
		return true;
	}

	markReported(scriptUrl, scriptHash) {
		const key = this.makeKey(scriptUrl, scriptHash);
		this.reported.set(key, Date.now());
		this._pruneIfNeeded();
		this._persistMaybe();
	}

	isInFlight(scriptUrl, scriptHash) {
		return this.inFlight.has(this.makeKey(scriptUrl, scriptHash));
	}

	markInFlight(scriptUrl, scriptHash) {
		this.inFlight.add(this.makeKey(scriptUrl, scriptHash));
	}

	clearInFlight(scriptUrl, scriptHash) {
		this.inFlight.delete(this.makeKey(scriptUrl, scriptHash));
	}

	_getStorageMode() {
		const mode = this.config?.get?.('scriptIntegrityDedupeStorage');
		if (mode === 'local' || mode === 'session' || mode === 'memory') return mode;
		return 'memory';
	}

	_getStorage() {
		const mode = this._getStorageMode();
		if (mode === 'memory') return null;
		try {
			if (typeof window === 'undefined') return null;
			return mode === 'local' ? window.localStorage : window.sessionStorage;
		} catch {
			return null;
		}
	}

	_getStorageKey() {
		const prefix = this.config?.get?.('scriptIntegrityDedupeKeyPrefix') || 'adr_sri_v1';
		return `${prefix}:reported`;
	}

	_getNumber(key, fallback) {
		try {
			const v = this.config?.get?.(key);
			const n = Number(v);
			return Number.isFinite(n) ? n : fallback;
		} catch {
			return fallback;
		}
	}

	_loadReportedFromStorage() {
		if (!this._storage || !this._storageKey) return;
		try {
			const raw = this._storage.getItem(this._storageKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			const entries = parsed && Array.isArray(parsed.entries) ? parsed.entries : null;
			if (!entries) return;

			const now = Date.now();
			for (const item of entries) {
				if (!Array.isArray(item) || item.length < 2) continue;
				const [key, ts] = item;
				if (typeof key !== 'string') continue;
				const t = Number(ts);
				if (!Number.isFinite(t) || t <= 0) continue;
				if (this._ttlMs > 0 && (now - t) > this._ttlMs) continue;
				this.reported.set(key, t);
			}
			this._pruneIfNeeded();
		} catch (e) {
			// Ignore storage parse issues; fall back to memory-only.
			this._debug('ScriptCache storage load failed', e);
		}
	}

	_persistMaybe() {
		if (!this._storage || !this._storageKey) return;
		try {
			const entries = Array.from(this.reported.entries());
			const payload = { v: 1, entries };
			this._storage.setItem(this._storageKey, JSON.stringify(payload));
		} catch (e) {
			// Ignore quota/disabled storage; keep working in memory.
			this._debug('ScriptCache storage persist failed', e);
		}
	}

	_pruneIfNeeded() {
		if (this.reported.size <= this._maxEntries) return;
		// Remove oldest entries first.
		const sorted = Array.from(this.reported.entries()).sort((a, b) => (a[1] || 0) - (b[1] || 0));
		const toRemove = Math.max(0, sorted.length - this._maxEntries);
		for (let i = 0; i < toRemove; i++) {
			this.reported.delete(sorted[i][0]);
		}
	}

	_debug(msg, err) {
		try {
			this.logger?.debug?.(msg, err);
		} catch {
		}
	}
}

export default ScriptCache;